/* src/components/pages/Estadisticacomprasenr.jsx - Modificado el 06-02-2026 */
/* ------------- consulta estadística de compras enrolado ------------------ */

import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Button,
  useTheme,
} from "@mui/material";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { useFeedback } from "../../context/FeedbackContext";
import NoData from "./NoData";

import { normalizeRut } from "../utils/rutUtils";

const meses = [
  "",
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

/* ================== Card reutilizable ================== */
const ChartCard = ({ title, subtitle, data, color }) => {
  const theme = useTheme();
  const ui = theme.custom.ui;

  return (
    <Card
      sx={{
        flex: "1 1 32%",
        minWidth: 320,
        borderTop: `3px solid ${ui.titleAccent}`,
      }}
    >
      <CardContent>
        <Typography sx={ui.cardTitleAccent}>{title}</Typography>
        <Typography sx={ui.cardSubtitle}>{subtitle}</Typography>

        {data?.length ? (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart
              data={data}
              margin={{ top: 20, right: 20, left: 10, bottom: 10 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={ui.chartGrid}
                vertical={false}
              />

              <XAxis
                dataKey="mes"
                tickFormatter={(m) => meses[m]}
                tick={{ fill: ui.chartTick, fontSize: 12 }}
              />

              <YAxis
                tick={{ fill: ui.chartTick, fontSize: 12 }}
                tickFormatter={(v) =>
                  new Intl.NumberFormat("es-CL", {
                    notation: "compact",
                    compactDisplay: "short",
                  }).format(v)
                }
              />

              <Tooltip
                labelFormatter={(label) => `Mes: ${meses[label]}`}
                formatter={(value) =>
                  `$${Number(value).toLocaleString("es-CL")}`
                }
              />

              <Line
                type="monotone"
                dataKey="total"
                stroke={color}
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <NoData mensaje="No hay datos" />
        )}
      </CardContent>
    </Card>
  );
};

/* ================== Componente principal ================== */
const Estadisticacomprasenr = () => {
  const { user } = useAuth();
  const { showFeedback } = useFeedback();
  const theme = useTheme();
  const ui = theme.custom.ui;

  const [anos, setAnos] = useState([]);
  const [anoSeleccionado, setAnoSeleccionado] = useState("");

  const [enrolados, setEnrolados] = useState([]);
  const [rutSeleccionado, setRutSeleccionado] = useState("");

  const [payload, setPayload] = useState(null);

  /* ======================================================
   * 1. Cargar años disponibles del cliente
   * ====================================================== */
  useEffect(() => {
    const fetchAnios = async () => {
      try {
        const res = await api.post("/librocompras/getanoscompras", {
          RutCli: normalizeRut(user.usuario),
          RutEnr: '',
          Tipousr: 2, /* Enrolado */
        });

        if (res.data?.rows?.length) {
          setAnos(res.data.rows);
        }
      } catch {
        showFeedback({
          message: "No fue posible obtener los años disponibles",
          severity: "error",
        });
      }
    };

    fetchAnios();
  }, [user.usuario, showFeedback]);

  /* ======================================================
   * 2. Cargar enrolados asociados al cliente
   * ====================================================== */
  useEffect(() => {
    const fetchEnrolados = async () => {
      try {
        const RutCli = normalizeRut(user.usuario);

        const res = await api.post("/enrolados/enroladosasociados", {
          RutCli,
          RutEnr: '',
          Tipousr: 1, /* Cliente */
        });

        if (res.data?.success && res.data.rows) {
          setEnrolados(res.data.rows);
        } else {
          setEnrolados([]);
        }
      } catch {
        showFeedback({
          message: "No fue posible obtener enrolados asociados",
          severity: "error",
        });
      }
    };

    fetchEnrolados();
  }, [user.usuario, showFeedback]);

  /* ======================================================
   * 3. Reset cards cuando cambia Año o Enrolado
   * ====================================================== */
  useEffect(() => {
    setPayload(null);
  }, [anoSeleccionado, rutSeleccionado]);

  /* ======================================================
   * 4. Acción Buscar (única consulta)
   * ====================================================== */
  const handleBuscar = async () => {
    if (!anoSeleccionado) {
      showFeedback({
        message: "Debe seleccionar un año",
        severity: "warning",
      });
      return;
    }

    if (!rutSeleccionado) {
      showFeedback({
        message: "Debe seleccionar un enrolado",
        severity: "warning",
      });
      return;
    }

    try {
      const res = await api.post("/librocompras/getestadisticacompras", {
        RutCli: normalizeRut(user.usuario),
        RutEnr: normalizeRut(rutSeleccionado),
        Tipousr: 2, /* Enrolado */
        Anoaconsultar: anoSeleccionado,
      });

      if (res.data?.success) {
        setPayload(res.data);
      }
    } catch {
      showFeedback({
        message: "No fue posible obtener estadísticas",
        severity: "error",
      });
    }
  };

  const series = payload?.series || {};
  const resumen = payload?.resumen || {};

  return (
    <Box>
      {/* ===================== CONTROLES ===================== */}
      <Stack direction="row" spacing={1} mb={2}>
        <TextField
          label="Rut Cliente"
          value={user.usuario}
          size="small"
          disabled
          sx={ui.readonlyField}
        />

        <TextField
          label="Cliente"
          value={user.nombre}
          size="small"
          disabled
          sx={{ ...ui.readonlyField, flexGrow: 1 }}
        />

        {/* Año */}
        <TextField
          select
          label="Año"
          value={anoSeleccionado}
          onChange={(e) => setAnoSeleccionado(e.target.value)}
          size="small"
          sx={{ width: 120 }}
        >
          {anos.map(({ ano }) => (
            <MenuItem key={ano} value={ano}>
              {ano}
            </MenuItem>
          ))}
        </TextField>

        {/* Enrolado */}
        <TextField
          select
          label="Enrolado"
          value={rutSeleccionado}
          size="small"
          onChange={(e) => setRutSeleccionado(e.target.value)}
          sx={{ minWidth: 260 }}
        >
          {enrolados.length > 0 ? (
            enrolados.map((item) => (
              <MenuItem key={item.rut_enr} value={item.rut_enr}>
                {item.nombre_enr || item.rut_enr}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No hay registros</MenuItem>
          )}
        </TextField>

        {/* Buscar */}
        <Button
          variant="contained"
          onClick={handleBuscar}
          sx={{
            minWidth: 120,
            fontWeight: 700,
            borderRadius: 2,
          }}
        >
          Buscar
        </Button>
      </Stack>

      {/* ===================== DASHBOARD ===================== */}
      {!payload ? (
        <NoData mensaje="Seleccione Año, Enrolado y presione Buscar" />
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <ChartCard
            title="Factura Electrónica (33)"
            subtitle={`Año ${anoSeleccionado}`}
            data={series.fa33}
            color={ui.primary}
          />

          <ChartCard
            title="Factura Exenta (34)"
            subtitle={`Año ${anoSeleccionado}`}
            data={series.fa34}
            color={ui.secondary}
          />

          <ChartCard
            title="Nota de Crédito (61)"
            subtitle={`Año ${anoSeleccionado}`}
            data={series.nc61}
            color={ui.error}
          />

          <ChartCard
            title="Nota de Débito (56)"
            subtitle={`Año ${anoSeleccionado}`}
            data={series.nd56}
            color={ui.warning}
          />

          <ChartCard
            title="Guía de Despacho (52)"
            subtitle={`Año ${anoSeleccionado}`}
            data={series.gd52}
            color={ui.info}
          />

          {/* ===================== Resumen anual completo ===================== */}
          <Card
            sx={{
              flex: "1 1 32%",
              minWidth: 320,
              borderTop: `3px solid ${ui.titleAccent}`,
            }}
          >
            <CardContent>
              <Typography sx={ui.cardTitleAccent}>
                Resumen anual
              </Typography>

              <Typography sx={ui.cardSubtitle}>
                Año {anoSeleccionado}
              </Typography>

              <Stack spacing={1.1} mt={2}>
                {/* ===================== Detalle por tipo de documento ===================== */}

                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    Factura Electrónica (33)
                  </Typography>
                  <Typography sx={{ fontWeight: 600, textAlign: "right" }}>
                    ${resumen.totalFa33?.toLocaleString("es-CL")}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    Factura Exenta Electrónica (34)
                  </Typography>
                  <Typography sx={{ fontWeight: 600, textAlign: "right" }}>
                    ${resumen.totalFa34?.toLocaleString("es-CL")}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    Nota de Débito Electrónica (56)
                  </Typography>
                  <Typography sx={{ fontWeight: 600, textAlign: "right" }}>
                    ${resumen.totalNd56?.toLocaleString("es-CL")}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    Nota de Crédito Electrónica (61)
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      textAlign: "right",
                      color:
                        resumen.totalNc61 > 0 ? "error.main" : "text.primary",
                    }}
                  >
                    -${resumen.totalNc61?.toLocaleString("es-CL")}
                  </Typography>
                </Stack>

                {/* ===================== Separador visual ===================== */}
                <Box
                  sx={{
                    borderTop: `1px solid ${ui.borderSubtle}`,
                    my: 1.5,
                  }}
                />

                {/* ===================== Totales consolidados ===================== */}
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontWeight: 600 }}>
                    Venta Neta Total
                  </Typography>
                  <Typography sx={{ fontWeight: 700, textAlign: "right" }}>
                    ${resumen.totalNeto?.toLocaleString("es-CL")}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    Promedio mensual
                  </Typography>
                  <Typography sx={{ fontWeight: 600, textAlign: "right" }}>
                    ${resumen.promedioMensual?.toLocaleString("es-CL")}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    Mes con mayor venta
                  </Typography>
                  <Typography sx={{ fontWeight: 600, textAlign: "right" }}>
                    {resumen.mesFuerte
                      ? `${resumen.mesFuerte.mes} $${resumen.mesFuerte.valor.toLocaleString(
                          "es-CL"
                        )}`
                      : "—"}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default Estadisticacomprasenr;
